<template>
    <view-container>
        <modal :parent="configurationStore.appContainerSelector" class="fit-content">
            <template #opener="{ open }">
                <div class="info-opener">
                    <div class="info-button" @click="open">
                        <info-icon />
                    </div>
                </div>
            </template>

            <template #header="{ close }">
                <view-header close-button @closed="close" />
            </template>

            <div class="modal-content">
                <start-dca-description />
            </div>
        </modal>

        <h1>{{ t('widget.dollar-cost-average.existing-plans.title') }}</h1>
        <p class="text center">{{ t('widget.dollar-cost-average.existing-plans.description') }}</p>

        <template v-if="authenticationStore.user.authenticated">
            <transitional name="fade" mode="out-in">
                <list-select v-if="ApiCommunicationStates.SUCCESSFUL === dcaCodes.communication" name="existing-plans" caret="right" @updated="setCurrentPlan">
                    <template v-for="item of dcaCodes.codes" #[item.code]>
                        <list-select-item :icon="item.cryptoCurrency" directory="currencies">
                            {{ getCryptoCurrencyName(item.cryptoCurrency) }}
                            <template #sub>
                                {{ item.code }}
                            </template>
                        </list-select-item>
                    </template>
                </list-select>

                <notification v-else-if="dcaCodes.communication === ApiCommunicationStates.FAILED" type="danger">
                    <translate keypath="widget.dollar-cost-average.existing-plans.retrieve-error">
                        <template #link>
                            <a
                                class="link"
                                :href="getPublicLink('support')"
                                target="_blank"
                                :title="t('widget.dollar-cost-average.existing-plans.retrieve-error.link')"
                            >
                                {{ t('widget.dollar-cost-average.existing-plans.retrieve-error.link') }}
                            </a>
                        </template>
                    </translate>
                </notification>

                <div v-else class="loading">
                    <spinner />
                </div>
            </transitional>
        </template>

        <template #footer v-if="!!authenticationStore.user.initialized">
            <transitional name="fade">
                <call-to-action v-if="authenticationStore.user.authenticated" @clicked="emit(EmitEvents.NEW)">
                    {{ t('widget.dollar-cost-average.existing-plans.button.new') }}
                </call-to-action>
                <call-to-action v-if="!authenticationStore.user.authenticated" @clicked="emit(EmitEvents.CLICKED)">
                    {{ t('widget.dollar-cost-average.existing-plans.button.login') }}
                </call-to-action>
            </transitional>
        </template>
    </view-container>
</template>

<script lang="ts" setup>
import StartDcaDescription from '@/dollar-cost-average/components/start-dca-description.vue';
import { useDca } from '@/dollar-cost-average/composables/dca';
import getCryptoCurrencyName from '@/dollar-cost-average/functions/currencies';
import { CallToAction, ListSelect, ListSelectItem, Notification, Spinner, Translate, ViewContainer, ViewHeader } from '@btcdirect/components';
import { InfoIcon } from '@btcdirect/components/icons';
import { useTranslations } from '@btcdirect/composables';
import Transitional from '@btcdirect/shared/src/components/transition/transitional.vue';
import { EmitEvents } from '@btcdirect/shared/src/components/types';
import { useSystemInfo } from '@btcdirect/shared/src/composables/api/system/system-info';
import { ApiCommunicationStates } from '@btcdirect/shared/src/composables/api/types';
import { useAuthenticationStore, useConfigurationStore } from '@btcdirect/stores';
import { Modal, ValidatedStringArrayFieldData } from 'out-of-the-vue';
import { onMounted } from 'vue';

const emit = defineEmits([EmitEvents.NEW, EmitEvents.UPDATED, EmitEvents.CLICKED]);

const configurationStore = useConfigurationStore();
const authenticationStore = useAuthenticationStore();
const { dcaCodes, dcaRequest, currentDca, requestExistingCodes } = useDca();
const { t } = useTranslations();
const { getPublicLink } = useSystemInfo();

const setCurrentPlan = (data: ValidatedStringArrayFieldData): void => {
    const found = dcaCodes.codes.find((item) => item.code === data.value[0]);
    if (!found) {
        return;
    }

    dcaRequest.cryptoCurrency = null;
    dcaRequest.fiatAmount = null;
    dcaRequest.fiatCurrency = null;
    dcaRequest.recurrence = null;
    dcaRequest.communication = null;
    dcaRequest.walletAddress = null;

    currentDca.bankBic = found.bankBic;
    currentDca.bankIban = found.bankIban;
    currentDca.bankName = found.bankName;
    currentDca.code = found.code;
    currentDca.cryptoCurrency = found.cryptoCurrency;
    currentDca.walletAddress = found.walletAddress;
    currentDca.destinationTag = found.destinationTag;
    currentDca.apiClientName = found.apiClientName;

    emit(EmitEvents.UPDATED);
};

onMounted(() => {
    if (authenticationStore.user.authenticated) {
        requestExistingCodes();
    }
});
</script>

<style lang="scss" scoped>
@use '@@scss/flex';

.dca-existing-plans {
    main > p {
        margin-bottom: 0.5em;
    }

    :deep(.component-header) {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
    }
}

.modal-content {
    padding-bottom: 3.5em;
    text-align: center;
}

.loading {
    @include flex.flex;
    @include flex.align($vertical: center);
    padding-top: 0.5em;

    .icon-spinner {
        width: 3rem;
        height: 3rem;
    }
}

.info-opener {
    @include flex.flex;
    @include flex.align($vertical: end);
    width: 100%;

    .info-button {
        @include flex.flex;
        @include flex.align(center, center);
        width: 2.25rem;
        height: 2.25rem;

        &:hover {
            cursor: pointer;
        }
    }
}
</style>
